/**
 * A TIMESHEET THAT KEEPS ITSELF TIDY (Karan 26/10/2026).
 *
 * "If no time is added to something for a few weeks, it is automatically
 * removed from the timesheet — some people don't cross them off and they have
 * huge lists." A new week used to be seeded with every live project you lead,
 * so a list only ever grew; the rows sat there at 0:00 week after week.
 *
 * The rule, one window of IDLE_WEEKS full weeks before the week on screen:
 *   a row stays when
 *     - it has anything on it THIS week: minutes, a note, a day note, the
 *       review or training mark, it was added by hand, or there is mileage or
 *       an expense on its project dated this week;
 *     - it is pinned ("Keep on my timesheet");
 *     - the person logged time to the project in any of the prior weeks;
 *     - the project is new to them: created (won) inside the window, or it has
 *       not been on any of their prior sheets — which is how a newly assigned
 *       lead project still shows up the first time.
 *   Otherwise it is taken off, and the sheet records it in `idleRemoved`, so
 *   the next weeks know it was seen (not new) and the person can be told.
 *
 * FAIL CONSERVATIVE (house rule 2): a missed tidy-up costs nothing, a row
 * taken off wrongly costs a person their hours. So if ANY prior week's read
 * failed, or the projects or expenses could not be read, nothing is dropped.
 * Only a DRAFT inside its editing window is ever trimmed — never a submitted,
 * approved, sent-back, locked or unlocked week.
 *
 * Pure: no Firestore, no DOM, so test/timesheet-idle.test.mjs can run it.
 */

export const IDLE_WEEKS = 3;
const DAY_MS = 86400000;

const pad2 = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const dateOf = (iso) => { const [y, m, d] = String(iso).split('-').map(Number); return new Date(y, m - 1, d); };

/** The Mondays of the IDLE_WEEKS weeks before `weekStart` ('YYYY-MM-DD'), nearest first. */
export function priorWeekStarts(weekStart, n = IDLE_WEEKS) {
  const out = [];
  for (let i = 1; i <= n; i++) { const d = dateOf(weekStart); d.setDate(d.getDate() - 7 * i); out.push(ymd(d)); }
  return out;
}

/** Minutes on a row (h:mm or decimal hours per day, as the grid stores them). */
export function rowMinutes(r) {
  let t = 0;
  for (const v of Object.values((r && r.days) || {})) {
    if (!v) continue;
    const s = String(v).trim();
    if (s.includes(':')) { const [h, m] = s.split(':'); t += (parseInt(h || 0, 10) || 0) * 60 + (parseInt(m || 0, 10) || 0); }
    else { const f = parseFloat(s); if (!isNaN(f)) t += Math.round(f * 60); }
  }
  return t;
}

const isAsp = (r) => !!r.internal || r.task === 'ASP time';
const aspKey = (r) => 'asp:' + String(r.internal || r.label || '').trim().toLowerCase();
const hasNo = (r) => r.number != null && String(r.number).trim() !== '' && String(r.number).trim() !== '—' && String(r.number).trim() !== 'ASP';

/**
 * Every key a row can be matched by. A project row by its id AND its number
 * (older rows carry only the number, timesheet-keys.js); practice time by its
 * category. Matching is at PROJECT level on purpose: time on any instruction
 * of a project keeps all of its rows — the cautious reading.
 */
export function rowKeys(r) {
  if (!r || typeof r !== 'object') return [];
  if (isAsp(r)) return [aspKey(r)];
  const keys = [];
  if (r.projectId) keys.push('p:' + r.projectId);
  if (hasNo(r)) keys.push('n:' + String(r.number).trim());
  return keys;
}
/** The one key a pin (or a removal record) is stored under. */
export const rowKey = (r) => rowKeys(r)[0] || null;

const anyIn = (keys, set) => keys.some((k) => set.has(k));

/**
 * What the prior weeks say. `priors` is one result per prior week:
 * { sheet } for a week read (null/undefined = no sheet that week), or
 * { failed: true } for a read that errored.
 */
export function priorEvidence(priors) {
  const timed = new Set(), seen = new Set();
  let failed = false;
  for (const p of (priors || [])) {
    if (!p || p.failed) { failed = true; continue; }
    const s = p.sheet;
    if (!s) continue;
    for (const r of (s.rows || [])) {
      const ks = rowKeys(r);
      ks.forEach((k) => seen.add(k));
      if (rowMinutes(r) > 0) ks.forEach((k) => timed.add(k));
    }
    for (const x of (s.idleRemoved || [])) rowKeys(x).concat(x && x.key ? [x.key] : []).forEach((k) => seen.add(k));
  }
  return { failed, timed, seen };
}

/** Something on the row this week, other than its being there. */
export function rowTouched(r) {
  if (!r) return false;
  if (rowMinutes(r) > 0) return true;
  if (r.note && String(r.note).trim()) return true;
  if (r.dayNotes && Object.values(r.dayNotes).some((v) => v && String(v).trim())) return true;
  return !!(r.review || r.training || r.added);
}

const toMs = (v) => {
  if (!v) return NaN;
  if (typeof v === 'number') return v;
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (typeof v.toDate === 'function') return v.toDate().getTime();
  if (v instanceof Date) return v.getTime();
  if (typeof v === 'object' && v.seconds != null) return Number(v.seconds) * 1000;
  return Date.parse(v);
};
/** Created (a project is created when its enquiry or quote is won) inside the window before weekStart. */
export function projectIsNew(project, weekStart, n = IDLE_WEEKS) {
  if (!project) return false;
  const since = dateOf(weekStart).getTime() - n * 7 * DAY_MS;
  return [project.createdAt, project.wonAt].some((v) => { const t = toMs(v); return Number.isFinite(t) && t >= since; });
}

/**
 * Why a row stays, or null when it goes. ctx:
 *   evidence   priorEvidence(...)
 *   pins       Set of row keys
 *   projectsById  { id → project } (for created/won dates)
 *   expenseKeys   Set of row keys with mileage/an expense dated this week
 *   weekStart
 */
export function keepReason(r, ctx) {
  const ks = rowKeys(r);
  if (!ks.length) return 'unknown';                 // nothing to match it by: leave it alone
  if (rowTouched(r)) return 'touched';
  if (ctx.expenseKeys && anyIn(ks, ctx.expenseKeys)) return 'expense';
  if (ctx.pins && anyIn(ks, ctx.pins)) return 'pinned';
  if (anyIn(ks, ctx.evidence.timed)) return 'recent-time';
  const p = r.projectId && ctx.projectsById ? ctx.projectsById[r.projectId] : null;
  if (projectIsNew(p, ctx.weekStart)) return 'new-project';
  if (!anyIn(ks, ctx.evidence.seen)) return 'new-to-you';
  return null;
}

/** Can this run drop anything at all? Every input must have been read. */
export function canDrop(ctx) {
  return !!(ctx && ctx.evidence && !ctx.evidence.failed && ctx.projectsOk && ctx.expensesOk && ctx.expenseKeys);
}

/** The record kept of a removed row: enough to put it back. */
export function removalRecord(r) {
  return {
    key: rowKey(r), projectId: r.projectId || null, number: r.number == null ? null : String(r.number),
    label: r.label || null, task: r.task || null, internal: r.internal || null,
    instructionSeq: r.instructionSeq == null ? null : Number(r.instructionSeq), instructionDesc: r.instructionDesc || null,
  };
}
/** A removal record back as a fresh, empty row. Restored rows count as added by hand. */
export function restoreRow(x) {
  return {
    projectId: x.projectId || null, number: x.number || (x.internal ? 'ASP' : '—'), label: x.label || 'Project',
    task: x.task || (x.internal ? 'ASP time' : 'Time'), ...(x.internal ? { internal: x.internal } : {}),
    instructionSeq: x.instructionSeq == null ? (x.internal ? null : 1) : Number(x.instructionSeq),
    instructionDesc: x.instructionDesc || null, days: {}, note: '', review: false, added: true,
  };
}

function mergeRemoved(existing, add) {
  const out = (existing || []).slice();
  for (const x of add) if (!out.some((y) => y.key === x.key && Number(y.instructionSeq || 0) === Number(x.instructionSeq || 0))) out.push(x);
  return out;
}

/**
 * The open week, already saved. Returns null when nothing may or need be
 * removed, else { rows, removed, idleRemoved } — the rows to keep, what was
 * taken off, and the sheet's updated removal record.
 *   opts.locked  the Tuesday-10:00 lock has passed (or the week was unlocked)
 */
export function trimDraft(sheet, ctx, { locked = false } = {}) {
  if (!sheet || sheet.status !== 'draft' || locked || sheet.unlocked) return null;
  if (!Array.isArray(sheet.rows) || !canDrop(ctx)) return null;
  const rows = [], removed = [];
  for (const r of sheet.rows) (keepReason(r, ctx) ? rows : removed).push(r);
  if (!removed.length) return null;
  const recs = removed.map(removalRecord);
  return { rows, removed: recs, idleRemoved: mergeRemoved(sheet.idleRemoved, recs) };
}

/**
 * The rows a brand-new week starts with.
 *   priors    as priorEvidence takes (nearest week first)
 *   seeded    rows from the old seeding (projects you lead, or the first live ones)
 *   pinned    rows for pinned projects / practice time
 *   isLive(projectId)  only live projects are carried forward
 *   hidden    Set of project ids the person crossed off (×): never carried
 * Carrying forward ADDS rows (always safe): any project or practice time with
 * minutes in a prior week, as its instruction, empty. Then the idle rule
 * filters the lot — unless canDrop(ctx) is false, when nothing is dropped.
 */
export function seedWeek({ priors, seeded = [], pinned = [], isLive = () => true, hidden = new Set() }, ctx) {
  const out = [];
  const slot = (r) => `${rowKey(r)}#${isAsp(r) ? '' : Number(r.instructionSeq || 1)}`;
  const have = new Set();
  const push = (r) => { const k = slot(r); if (!rowKey(r) || have.has(k)) return; have.add(k); out.push(r); };
  for (const p of (priors || [])) {
    if (!p || p.failed || !p.sheet) continue;
    for (const r of (p.sheet.rows || [])) {
      if (rowMinutes(r) <= 0) continue;
      if (!isAsp(r) && (!r.projectId || hidden.has(r.projectId) || !isLive(r.projectId))) continue;
      push(carriedRow(r));
    }
  }
  seeded.forEach(push);
  pinned.forEach(push);
  if (!canDrop(ctx)) return { rows: out, removed: [] };
  const rows = [], removed = [];
  for (const r of out) (keepReason(r, ctx) ? rows : removed).push(r);
  return { rows, removed: removed.map(removalRecord) };
}
// A prior week's row as this week's: same project and instruction, nothing logged.
function carriedRow(r) {
  const x = restoreRow(removalRecord(r));
  delete x.added;   // carried, not added by hand: the idle rule still applies next week
  return x;
}

/** "3 projects with no time for 3 weeks were taken off: 8224, 8177, 7993. Add project brings one back." */
export function idleNotice(removed, n = IDLE_WEEKS) {
  const list = (removed || []).filter(Boolean);
  if (!list.length) return '';
  const names = list.map((x) => (x.internal || !hasNo(x) ? (x.label || 'Practice time') : String(x.number).trim()));
  const uniq = [...new Set(names)];
  const one = list.length === 1;
  return `${list.length} ${one ? 'project' : 'projects'} with no time for ${n} weeks ${one ? 'was' : 'were'} taken off: ${uniq.join(', ')}. Add project brings one back.`;
}

/** Rows keys with mileage or an expense dated inside the week (Mon–Sun). */
export function expenseKeysForWeek(expenses, weekStart) {
  const start = weekStart; const endD = dateOf(weekStart); endD.setDate(endD.getDate() + 6); const end = ymd(endD);
  const keys = new Set();
  for (const x of (expenses || [])) {
    const d = String((x && x.date) || '').slice(0, 10);
    if (!d || d < start || d > end) continue;
    if (x.projectId) keys.add('p:' + x.projectId);
    if (x.projectNo) keys.add('n:' + String(x.projectNo).trim());
  }
  return keys;
}
